import React, { useState, useEffect } from 'react';
import { safeTrim } from '../utils/validation';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Network, Plus, Edit2, Trash2, RefreshCw, Upload, 
  Check, X, Server, ChevronDown, ChevronUp, Shield, Settings
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IPPoolManagement = () => {
  const [pools, setPools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncingId, setSyncingId] = useState(null);
  const [mikrotikRouters, setMikrotikRouters] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [selectedRouter, setSelectedRouter] = useState('all');
  const [isSyncAllDialogOpen, setIsSyncAllDialogOpen] = useState(false);
  
  // Isolir Pool Settings State
  const [isIsolirDialogOpen, setIsIsolirDialogOpen] = useState(false);
  const [isolirLoading, setIsolirLoading] = useState(false);
  const [isolirSaving, setIsolirSaving] = useState(false);
  const [isolirSetupLoading, setIsolirSetupLoading] = useState(false);
  const [isolirConfig, setIsolirConfig] = useState({
    pool_name: 'pool-isolir',
    range_start: '10.99.99.2',
    range_end: '10.99.99.254'
  });
  const [isolirSetupResult, setIsolirSetupResult] = useState(null);
  
  // Check if user is Super Admin
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = user.role?.toLowerCase() === 'super_admin' || user.role?.toLowerCase() === 'superadmin';
  
  const [formData, setFormData] = useState({
    name: '',
    range_start: '',
    range_end: '',
    local_address: '10.10.10.1',
    description: '',
    is_active: true,
    selected_routers: [], // Array of router IDs to sync
    tenant_id: '' // Tenant owner of this pool
  });

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchPools();
    fetchRouters();
    fetchTenants();
    fetchIsolirConfig();
  }, []);

  const fetchPools = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/v2/ip-pools`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPools(response.data.data || []);
    } catch (error) {
      console.error('Error fetching pools:', error);
      toast.error('Gagal memuat data IP Pool');
    } finally {
      setLoading(false);
    }
  };

  const fetchRouters = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/v2/mikrotiks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMikrotikRouters(response.data.data || response.data || []);
    } catch (error) {
      console.error('Error fetching routers:', error);
    }
  };

  const fetchTenants = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/v2/tenants?is_active=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTenants(response.data.data || []);
    } catch (error) {
      console.error('Error fetching tenants:', error);
    }
  };

  const fetchIsolirConfig = async () => {
    try {
      setIsolirLoading(true);
      const response = await axios.get(`${API_URL}/api/settings/isolir-pool`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success && response.data.data) {
        setIsolirConfig(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching isolir config:', error);
    } finally {
      setIsolirLoading(false);
    }
  };

  const saveIsolirConfig = async () => {
    if (!isolirConfig.pool_name.trim()) {
      toast.error('Nama Pool harus diisi');
      return;
    }
    
    try {
      setIsolirSaving(true);
      const response = await axios.put(
        `${API_URL}/api/settings/isolir-pool`,
        isolirConfig,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('Konfigurasi Pool Isolir berhasil disimpan');
        setIsIsolirDialogOpen(false);
      } else {
        toast.error(response.data.message || 'Gagal menyimpan konfigurasi');
      }
    } catch (error) {
      console.error('Error saving isolir config:', error);
      const msg = error.response?.data?.detail || 'Gagal menyimpan konfigurasi';
      toast.error(msg);
    } finally {
      setIsolirSaving(false);
    }
  };

  const setupIsolirToMikrotik = async () => {
    try {
      setIsolirSetupLoading(true);
      setIsolirSetupResult(null);
      
      // Only Super Admin can save config, tenant admin just syncs
      if (isSuperAdmin) {
        await axios.put(
          `${API_URL}/api/settings/isolir-pool`,
          isolirConfig,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      // Then setup to MikroTik - use config values and selected router
      const poolRange = `${isolirConfig.range_start}-${isolirConfig.range_end}`;
      const requestData = {
        pool_range: poolRange,
        local_address: isolirConfig.range_start,
        rate_limit: '256k/256k',
        dns_server: isolirConfig.range_start
      };
      
      // Add router_id if specific router is selected
      if (selectedRouter && selectedRouter !== 'all') {
        requestData.router_id = selectedRouter;
      }
      
      const response = await axios.post(
        `${API_URL}/api/mikrotik/setup-isolir-profile`,
        requestData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setIsolirSetupResult({
          success: true,
          details: response.data.details || [],
          next_steps: response.data.next_steps || []
        });
        toast.success('✅ Pool Isolir berhasil di-setup ke MikroTik!');
      } else {
        setIsolirSetupResult({
          success: false,
          message: response.data.message
        });
        toast.error(response.data.message || 'Gagal setup ke MikroTik');
      }
    } catch (error) {
      console.error('Error setting up isolir to MikroTik:', error);
      const msg = error.response?.data?.detail || error.response?.data?.message || 'Gagal setup ke MikroTik';
      setIsolirSetupResult({
        success: false,
        message: msg
      });
      toast.error(msg);
    } finally {
      setIsolirSetupLoading(false);
    }
  };

  const checkIsolirSetup = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/mikrotik/check-isolir-setup`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      console.error('Error checking isolir setup:', error);
      return null;
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      range_start: '',
      range_end: '',
      local_address: '10.10.10.1',
      description: '',
      is_active: true,
      selected_routers: [],
      tenant_id: ''
    });
    setIsEditMode(false);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!safeTrim(formData.name)) {
      toast.error('Nama Pool harus diisi');
      return;
    }
    if (!safeTrim(formData.range_start) || !safeTrim(formData.range_end)) {
      toast.error('Range IP harus diisi');
      return;
    }
    
    try {
      if (isEditMode) {
        await axios.put(`${API_URL}/api/v2/ip-pools/${editingId}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('IP Pool berhasil diupdate');
      } else {
        await axios.post(`${API_URL}/api/v2/ip-pools`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('IP Pool berhasil ditambahkan');
      }
      
      setIsDialogOpen(false);
      resetForm();
      fetchPools();
    } catch (error) {
      console.error('Error saving pool:', error);
      const msg = error.response?.data?.detail || 'Gagal menyimpan IP Pool';
      toast.error(msg);
    }
  };

  const handleEdit = (pool) => {
    // Parse synced_routers to get router IDs
    let selectedRouterIds = [];
    if (pool.synced_routers) {
      const routerNames = pool.synced_routers.split(',').map(r => r.trim());
      selectedRouterIds = mikrotikRouters
        .filter(r => routerNames.includes(r.name))
        .map(r => r.id);
    }
    
    setFormData({
      name: pool.name,
      range_start: pool.range_start,
      range_end: pool.range_end,
      local_address: pool.local_address || '10.10.10.1',
      description: pool.description || '',
      is_active: pool.is_active,
      selected_routers: selectedRouterIds,
      tenant_id: pool.tenant_id || ''
    });
    setIsEditMode(true);
    setEditingId(pool.id);
    setIsDialogOpen(true);
  };

  const handleDelete = async (pool) => {
    if (!window.confirm(`Hapus IP Pool "${pool.name}"?`)) return;
    
    try {
      await axios.delete(`${API_URL}/api/v2/ip-pools/${pool.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('IP Pool berhasil dihapus');
      fetchPools();
    } catch (error) {
      console.error('Error deleting pool:', error);
      toast.error('Gagal menghapus IP Pool');
    }
  };

  const handleSync = async (pool) => {
    setSyncingId(pool.id);
    try {
      const response = await axios.post(
        `${API_URL}/api/v2/ip-pools/${pool.id}/sync`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const result = response.data;
      if (result.created_on?.length > 0) {
        toast.success(`✅ Sync berhasil: ${result.created_on.join(', ')}`, { duration: 5000 });
      }
      if (result.failed_on?.length > 0) {
        toast.warning(`⚠️ Gagal di: ${result.failed_on.join(', ')}`, { duration: 5000 });
      }
      
      fetchPools();
    } catch (error) {
      console.error('Error syncing pool:', error);
      toast.error('Gagal sync IP Pool ke MikroTik');
    } finally {
      setSyncingId(null);
    }
  };

  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      const params = selectedRouter !== 'all' ? { router_id: selectedRouter } : {};
      const response = await axios.post(
        `${API_URL}/api/v2/ip-pools/sync-all`,
        params,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const result = response.data;
      toast.success(`✅ ${result.message}`, { duration: 5000 });
      
      setIsSyncAllDialogOpen(false);
      fetchPools();
    } catch (error) {
      console.error('Error syncing all pools:', error);
      toast.error('Gagal sync semua IP Pool');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="p-4 md:p-6 bg-slate-50 min-h-screen pb-20 dark:bg-slate-900">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-cyan-600 rounded-xl shadow-lg shadow-cyan-200">
            <Network className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800 dark:text-slate-200">IP Pool Management</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Kelola IP Pool untuk PPPoE/Hotspot ({pools.length} pool)
            </p>
          </div>
        </div>
        
        <div className="flex gap-2 flex-wrap">
          <Button 
            variant="outline" 
            onClick={() => setIsIsolirDialogOpen(true)}
            className="border-orange-200 text-orange-700 hover:bg-orange-50 dark:text-orange-300 dark:hover:bg-orange-900/30"
            data-testid="btn-kelola-pool-isolir"
          >
            <Shield className="w-4 h-4 mr-2" />
            Pool Isolir
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setIsSyncAllDialogOpen(true)}
            className="border-cyan-200 text-cyan-700 hover:bg-cyan-50 dark:text-cyan-300 dark:hover:bg-cyan-900/30"
          >
            <Upload className="w-4 h-4 mr-2" />
            Sync Semua
          </Button>
          <Button 
            onClick={() => { resetForm(); setIsDialogOpen(true); }}
            className="bg-cyan-600 hover:bg-cyan-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Tambah Pool
          </Button>
        </div>
      </div>

      {/* Isolir Pool Config Card */}
      <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl p-4 mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg dark:bg-orange-900/40">
              <Shield className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800 dark:text-slate-200">Pool Isolir Aktif</h3>
              <p className="text-sm text-slate-600 dark:text-slate-300">
                Pool yang digunakan untuk isolir pelanggan: <code className="px-2 py-0.5 bg-orange-100 rounded text-orange-800 font-mono text-sm dark:bg-orange-900/40">{isolirConfig.pool_name}</code>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-right text-sm text-slate-500 hidden sm:block dark:text-slate-400">
              <span>Range: </span>
              <code className="text-slate-700 dark:text-slate-200">{isolirConfig.range_start} - {isolirConfig.range_end}</code>
            </div>
            <Button 
              size="sm"
              variant="outline"
              onClick={() => setIsIsolirDialogOpen(true)}
              className="border-orange-300 text-orange-700 hover:bg-orange-100 dark:text-orange-300 dark:hover:bg-orange-900/40"
              data-testid="btn-edit-pool-isolir"
            >
              <Settings className="w-4 h-4 mr-1" />
              Sync
            </Button>
          </div>
        </div>
      </div>

      {/* Pool List */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden dark:bg-slate-800 dark:border-slate-700">
        {loading ? (
          <div className="p-8 text-center">
            <RefreshCw className="w-8 h-8 animate-spin text-cyan-600 mx-auto mb-3 dark:text-cyan-400" />
            <p className="text-slate-500 dark:text-slate-400">Memuat data...</p>
          </div>
        ) : pools.length === 0 ? (
          <div className="p-8 text-center">
            <Network className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 dark:text-slate-400">Belum ada IP Pool</p>
            <p className="text-sm text-slate-400 mt-1 dark:text-slate-500">Klik "Tambah Pool" untuk membuat IP Pool baru</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b dark:bg-slate-900">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase dark:text-slate-300">Nama Pool</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase dark:text-slate-300">IP Range</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase dark:text-slate-300">Gateway</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase dark:text-slate-300">Router</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase dark:text-slate-300">Status</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase dark:text-slate-300">Synced</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase dark:text-slate-300">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {pools.map((pool) => (
                  <tr key={pool.id} className="hover:bg-slate-50 transition-colors dark:hover:bg-slate-700/50">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-semibold text-slate-800 dark:text-slate-200">{pool.name}</p>
                        {pool.description && (
                          <p className="text-xs text-slate-500 mt-0.5 dark:text-slate-400">{pool.description}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <code className="px-2 py-1 bg-slate-100 rounded text-sm text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                        {pool.range_start} - {pool.range_end}
                      </code>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-slate-600 dark:text-slate-300">{pool.local_address || '-'}</span>
                    </td>
                    <td className="px-4 py-3">
                      {pool.synced_routers ? (
                        <div className="flex flex-wrap gap-1">
                          {pool.synced_routers.split(',').map((router, idx) => (
                            <span key={idx} className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded text-xs font-medium dark:bg-indigo-900/40 dark:text-indigo-300">
                              {router.trim()}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400 dark:text-slate-500">Belum dipilih</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {pool.is_active ? (
                        <Badge className="bg-green-100 text-green-700 hover:bg-green-100 dark:bg-green-900/40 dark:text-green-300 dark:hover:bg-green-900/40">Aktif</Badge>
                      ) : (
                        <Badge variant="secondary">Nonaktif</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {pool.is_synced ? (
                        <div className="flex items-center justify-center gap-1">
                          <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                          <span className="text-xs text-green-600 dark:text-green-400">Synced</span>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-1">
                          <X className="w-4 h-4 text-slate-400 dark:text-slate-500" />
                          <span className="text-xs text-slate-400 dark:text-slate-500">Belum</span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleSync(pool)}
                          disabled={syncingId === pool.id}
                          className="h-8 px-2 text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50 dark:text-cyan-400 dark:hover:bg-cyan-900/30"
                          title="Sync ke MikroTik"
                        >
                          <Upload className={`w-4 h-4 ${syncingId === pool.id ? 'animate-pulse' : ''}`} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEdit(pool)}
                          className="h-8 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/30"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(pool)}
                          className="h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Network className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
              {isEditMode ? 'Edit IP Pool' : 'Tambah IP Pool'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label className="text-sm font-medium">Nama Pool *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Contoh: POOL-SILVER, RadiusLink"
                className="mt-1"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium">IP Awal *</Label>
                <Input
                  value={formData.range_start}
                  onChange={(e) => setFormData({...formData, range_start: e.target.value})}
                  placeholder="172.16.0.2"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">IP Akhir *</Label>
                <Input
                  value={formData.range_end}
                  onChange={(e) => setFormData({...formData, range_end: e.target.value})}
                  placeholder="172.16.31.254"
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-sm font-medium">Local Address (Gateway)</Label>
              <Input
                value={formData.local_address}
                onChange={(e) => setFormData({...formData, local_address: e.target.value})}
                placeholder="10.10.10.1"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label className="text-sm font-medium">Deskripsi</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Keterangan pool (opsional)"
                className="mt-1"
              />
            </div>
            
            {/* Tenant Selection - Only visible for Super Admin */}
            {tenants.length > 0 && (
              <div>
                <Label className="text-sm font-medium">Tenant (Pemilik Pool) *</Label>
                <p className="text-xs text-slate-500 mb-2 dark:text-slate-400">Pilih tenant yang memiliki pool ini. Pool hanya akan terlihat oleh tenant yang dipilih.</p>
                <select
                  value={formData.tenant_id}
                  onChange={(e) => setFormData({...formData, tenant_id: e.target.value})}
                  className="w-full mt-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  required
                >
                  <option value="">-- Pilih Tenant --</option>
                  {tenants.map(tenant => (
                    <option key={tenant.uuid} value={tenant.uuid}>
                      {tenant.tenant_code} - {tenant.tenant_name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            <label className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                className="w-4 h-4 text-cyan-600 rounded dark:text-cyan-400"
              />
              <span className="text-sm">Pool Aktif</span>
            </label>
            
            {/* Router Selection */}
            <div>
              <Label className="text-sm font-medium">Sync ke Router *</Label>
              <p className="text-xs text-slate-500 mb-2 dark:text-slate-400">Pilih router yang akan di-sync. Pool hanya akan dibuat di router yang dipilih.</p>
              <div className="border rounded-lg p-2 max-h-40 overflow-y-auto space-y-1">
                {mikrotikRouters.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-2 dark:text-slate-500">Tidak ada router tersedia</p>
                ) : (
                  mikrotikRouters.filter(r => r.is_active).map(router => (
                    <label key={router.id} className="flex items-center gap-2 p-1.5 hover:bg-slate-50 rounded cursor-pointer dark:hover:bg-slate-700/50">
                      <input
                        type="checkbox"
                        checked={formData.selected_routers.includes(router.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({...formData, selected_routers: [...formData.selected_routers, router.id]});
                          } else {
                            setFormData({...formData, selected_routers: formData.selected_routers.filter(id => id !== router.id)});
                          }
                        }}
                        className="w-4 h-4 text-cyan-600 rounded dark:text-cyan-400"
                      />
                      <span className="text-sm">{router.name}</span>
                      <span className="text-xs text-slate-400 dark:text-slate-500">({router.host})</span>
                    </label>
                  ))
                )}
              </div>
              {formData.selected_routers.length > 0 && (
                <p className="text-xs text-green-600 mt-1 dark:text-green-400">{formData.selected_routers.length} router dipilih</p>
              )}
            </div>
            
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Batal
              </Button>
              <Button type="submit" className="bg-cyan-600 hover:bg-cyan-700">
                {isEditMode ? 'Update' : 'Simpan'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Sync All Dialog */}
      <Dialog open={isSyncAllDialogOpen} onOpenChange={setIsSyncAllDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
              Sync Semua IP Pool ke MikroTik
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-sm font-medium">Pilih Router</Label>
              <select
                value={selectedRouter}
                onChange={(e) => setSelectedRouter(e.target.value)}
                className="w-full mt-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
              >
                <option value="all">Semua Router Aktif</option>
                {mikrotikRouters.filter(r => r.is_active).map(router => (
                  <option key={router.id} value={router.id}>
                    {router.name || router.router_name} ({router.host})
                  </option>
                ))}
              </select>
            </div>
            
            <div className="p-3 bg-cyan-50 border border-cyan-200 rounded-lg dark:bg-cyan-900/30">
              <p className="text-sm text-cyan-800">
                <strong>{pools.filter(p => p.is_active).length}</strong> IP Pool aktif akan di-sync
              </p>
              <p className="text-xs text-cyan-600 mt-1 dark:text-cyan-400">
                Pool yang sudah ada di router akan di-update range-nya
              </p>
            </div>
            
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setIsSyncAllDialogOpen(false)}>
                Batal
              </Button>
              <Button 
                onClick={handleSyncAll}
                disabled={syncing}
                className="bg-cyan-600 hover:bg-cyan-700"
              >
                {syncing ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Syncing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Sync Sekarang
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Isolir Pool Config Dialog */}
      <Dialog open={isIsolirDialogOpen} onOpenChange={(open) => { setIsIsolirDialogOpen(open); if (!open) setIsolirSetupResult(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              {isSuperAdmin ? 'Konfigurasi Pool Isolir' : 'Sync Pool Isolir ke MikroTik'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className={`p-3 border rounded-lg ${isSuperAdmin ? "bg-orange-50 border-orange-200" : "bg-blue-50 border-blue-200"}`}>
              <p className={`text-sm ${isSuperAdmin ? "text-orange-800" : "text-blue-800"}`}>
                {isSuperAdmin 
                  ? 'Pool isolir digunakan untuk mengarahkan pelanggan yang di-isolir ke halaman notifikasi pembayaran.'
                  : 'Pool Isolir bersifat global. Anda hanya bisa sync pool ini ke MikroTik Anda.'}
              </p>
            </div>
            
            <div>
              <Label className="text-sm font-medium">Nama Pool {isSuperAdmin && '*'}</Label>
              <Input
                value={isolirConfig.pool_name}
                onChange={(e) => isSuperAdmin && setIsolirConfig({...isolirConfig, pool_name: e.target.value})}
                placeholder="pool-isolir"
                className={`mt-1 ${!isSuperAdmin ? "bg-gray-100 cursor-not-allowed" : ""}`}
                disabled={!isSuperAdmin}
                data-testid="input-isolir-pool-name"
              />
              {isSuperAdmin && (
                <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">
                  Nama pool harus sama dengan yang ada di MikroTik
                </p>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium">IP Awal</Label>
                <Input
                  value={isolirConfig.range_start}
                  onChange={(e) => isSuperAdmin && setIsolirConfig({...isolirConfig, range_start: e.target.value})}
                  placeholder="10.99.99.2"
                  className={`mt-1 ${!isSuperAdmin ? "bg-gray-100 cursor-not-allowed" : ""}`}
                  disabled={!isSuperAdmin}
                  data-testid="input-isolir-range-start"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">IP Akhir</Label>
                <Input
                  value={isolirConfig.range_end}
                  onChange={(e) => isSuperAdmin && setIsolirConfig({...isolirConfig, range_end: e.target.value})}
                  placeholder="10.99.99.254"
                  className={`mt-1 ${!isSuperAdmin ? "bg-gray-100 cursor-not-allowed" : ""}`}
                  disabled={!isSuperAdmin}
                  data-testid="input-isolir-range-end"
                />
              </div>
            </div>

            {/* Setup Result - Scrollable */}
            {isolirSetupResult && (
              <div className={`p-3 rounded-lg border max-h-48 overflow-y-auto ${isolirSetupResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                {isolirSetupResult.success ? (
                  <>
                    <p className="text-sm font-medium text-green-800 flex items-center gap-2 sticky top-0 bg-green-50 pb-1 dark:bg-green-900/30">
                      <Check className="w-4 h-4" /> Setup Berhasil!
                    </p>
                    <ul className="mt-2 space-y-1 text-xs">
                      {isolirSetupResult.details?.map((detail, idx) => (
                        <li key={idx} className="text-xs text-green-700 dark:text-green-300">{detail}</li>
                      ))}
                    </ul>
                    {isolirSetupResult.next_steps?.length > 0 && (
                      <div className="mt-3 pt-2 border-t border-green-200">
                        <p className="text-xs font-medium text-green-800">Langkah selanjutnya:</p>
                        <ul className="mt-1 space-y-1">
                          {isolirSetupResult.next_steps.map((step, idx) => (
                            <li key={idx} className="text-xs text-green-600 dark:text-green-400">• {step}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-red-800 flex items-center gap-2">
                    <X className="w-4 h-4" /> {isolirSetupResult.message}
                  </p>
                )}
              </div>
            )}

            {/* Target Router Selection */}
            <div className="pt-2">
              <Label className="text-sm font-medium">Target Router</Label>
              <select
                value={selectedRouter}
                onChange={(e) => setSelectedRouter(e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm dark:border-slate-600"
                data-testid="select-isolir-router"
              >
                <option value="all">🌐 Semua Router Aktif</option>
                {mikrotikRouters.map((router) => (
                  <option key={router.id} value={router.id}>
                    📡 {router.name} ({router.host})
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">
                Pilih router target untuk setup pool isolir
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row justify-between gap-2 pt-4 border-t">
              <Button 
                onClick={setupIsolirToMikrotik}
                disabled={isolirSetupLoading || !isolirConfig.pool_name.trim()}
                variant="outline"
                className="border-blue-300 text-blue-700 hover:bg-blue-50 dark:text-blue-300 dark:hover:bg-blue-900/30"
                data-testid="btn-setup-isolir-mikrotik"
              >
                {isolirSetupLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Menyiapkan...
                  </>
                ) : (
                  <>
                    <Server className="w-4 h-4 mr-2" />
                    Setup ke MikroTik
                  </>
                )}
              </Button>
              
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setIsIsolirDialogOpen(false)}>
                  Tutup
                </Button>
                {isSuperAdmin && (
                  <Button 
                    onClick={saveIsolirConfig}
                    disabled={isolirSaving}
                    className="bg-orange-600 hover:bg-orange-700"
                    data-testid="btn-save-isolir-config"
                  >
                    {isolirSaving ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Menyimpan...
                      </>
                    ) : (
                      'Simpan Config'
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IPPoolManagement;
